import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { STORAGE_KEYS } from '@personal-trainer-app/shared';

import { AppShellContext } from '../../components';
import { BottomTabBar } from '../../components/BottomTabBar';
import { Header } from '../../components/Header';
import { MobileDrawer } from '../../components/MobileDrawer';
import { Sidebar } from '../../components/Sidebar';

import type { PrivateLayoutProps } from './PrivateLayout.types';
import type { HeaderConfig, ViewMode } from '@/types/layout';

import { ImpersonationBanner } from '@/components/auth/ImpersonationBanner';
import { PaywallRouteGate } from '@/components/paywall/PaywallRouteGate';
import { useBreakpoint } from '@/hooks';
import { type SidebarBehavior } from '@/hooks';
import { useAuth } from '@/hooks/auth';
import { LAYOUT_DIMENSIONS } from '@/types/layout';
import { isDashboardRoute } from '@/utils';

/**
 * Compute sidebar behavior based on viewport width.
 *
 * Rules:
 * - Viewport >= 768px (md breakpoint): sidebar expandable (72px or 240px)
 * - Viewport < 768px: sidebar hidden (use mobile drawer flyout)
 */
function computeSidebarBehavior(viewportWidth: number): SidebarBehavior {
  // Viewport >= 768px (md breakpoint): sidebar can be expanded/collapsed
  if (viewportWidth >= 768) {
    return 'expandable';
  }

  // Smaller viewports: sidebar hidden, use mobile drawer flyout
  return 'hidden';
}

/**
 * PrivateLayout - Authenticated layout for protected pages
 *
 * Uses content-width-based breakpoints for responsive layout:
 * - **Mobile** (content < 600px): Header + Content + Bottom Tab Bar + Mobile Drawer
 * - **Tablet** (content 600-899px): Header + Collapsed Sidebar (72px icons) + Content
 * - **Desktop** (content >= 900px): Header + Expandable Sidebar (72-240px) + Content
 *
 * Content width = viewport width - sidebar width, ensuring the main content
 * area has sufficient space regardless of sidebar state.
 *
 * Uses AppShellContext for shared state management between layout components.
 *
 * @example
 * ```tsx
 * <PrivateLayout
 *   header={<PrivateHeader />}
 *   sidebarItems={navItems}
 *   sidebarBrand={{ name: 'Dō', tagline: 'Personal Training' }}
 *   tabBarItems={tabItems}
 *   tabBarActionButton={{ icon: <BoltIcon />, onClick: handleStart, ariaLabel: 'Start Workout' }}
 * />
 * ```
 */
export function PrivateLayout({
  header,
  sidebarItems = [],
  sidebarBrand,
  activeSidebarItemId,
  onSidebarItemClick,
  tabBarItems = [],
  activeTabBarItemId,
  onTabBarItemClick,
  tabBarActionButton,
  drawerItems,
  onLogout,
  logoutLabel = null,
  logo,
  className = '',
}: PrivateLayoutProps): React.JSX.Element {
  const location = useLocation();

  const { user } = useAuth();
  const username = user?.username;
  const { width: viewportWidth } = useBreakpoint();

  // Compute sidebar behavior based on content-width rules
  const sidebarBehavior = useMemo(
    () => computeSidebarBehavior(viewportWidth),
    [viewportWidth]
  );

  // Derived states from sidebar behavior
  const showSidebar = sidebarBehavior !== 'hidden';
  const showTabBar = sidebarBehavior === 'hidden';
  const canExpandSidebar = sidebarBehavior === 'expandable';

  // Sidebar state (tablet/desktop)
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  // Mobile drawer state
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // The impersonation strip sits above the fixed sidebar and header, which
  // are offset by its measured height (0 when it isn't showing).
  const [bannerHeight, setBannerHeight] = useState(0);

  // Track previous username to distinguish load vs save scenarios
  const prevUsernameRef = useRef<string | undefined>(undefined);

  // Load and persist sidebar state to localStorage
  // - When username changes: load stored preference
  // - When sidebarExpanded changes (same user): save to localStorage
  useEffect(() => {
    if (!username) {
      prevUsernameRef.current = undefined;
      return;
    }

    const key = `${STORAGE_KEYS.SIDEBAR_EXPANDED}:${username}`;
    const isNewUser = prevUsernameRef.current !== username;

    if (isNewUser) {
      // Load stored preference for this user
      try {
        const stored = localStorage.getItem(key);
        if (stored !== null) {
          setSidebarExpanded(JSON.parse(stored) as boolean);
        }
      } catch {
        // Silently fail if localStorage is unavailable
      }
      prevUsernameRef.current = username;
    } else {
      // Save current preference
      try {
        localStorage.setItem(key, JSON.stringify(sidebarExpanded));
      } catch {
        // Silently fail if localStorage is unavailable
      }
    }
  }, [username, sidebarExpanded]);

  // Dashboard view mode state (shared via AppShellContext)
  // dashboardViewMode: current mode for display (set by DashboardPage)
  // onDashboardViewModeChange: callback for user actions (registered by DashboardPage)
  const [dashboardViewMode, setDashboardViewMode] = useState<ViewMode | null>(
    null
  );
  const [onDashboardViewModeChange, setOnDashboardViewModeChange] = useState<
    ((mode: ViewMode) => void) | null
  >(null);

  // Header config pushed by form pages to take over the shell header (action variant)
  const [headerConfig, setHeaderConfig] = useState<HeaderConfig | null>(null);

  // Tab bar visibility: shown on mobile, but suppressed when a page requests a
  // full-screen editor via headerConfig.hideTabBar (drives both the tab bar
  // render and the content paddingBottom that reserves space for it).
  const tabBarVisible = showTabBar && !headerConfig?.hideTabBar;

  // Show view mode dropup only on dashboard
  const isDashboard = isDashboardRoute(location.pathname);

  const toggleSidebar = useCallback(() => {
    if (canExpandSidebar) {
      setSidebarExpanded((prev) => !prev);
    }
  }, [canExpandSidebar]);

  const toggleMobileDrawer = useCallback(() => {
    setMobileDrawerOpen((prev) => !prev);
  }, []);

  // Context value for child components
  const contextValue = useMemo(
    () => ({
      sidebarBehavior,
      sidebarExpanded,
      toggleSidebar,
      setSidebarExpanded,
      mobileDrawerOpen,
      toggleMobileDrawer,
      setMobileDrawerOpen,
      dashboardViewMode,
      setDashboardViewMode,
      onDashboardViewModeChange,
      setOnDashboardViewModeChange,
      headerConfig,
      setHeaderConfig,
    }),
    [
      sidebarBehavior,
      sidebarExpanded,
      toggleSidebar,
      mobileDrawerOpen,
      toggleMobileDrawer,
      dashboardViewMode,
      onDashboardViewModeChange,
      headerConfig,
    ]
  );

  // Calculate sidebar width based on behavior and expansion state
  const sidebarWidth = useMemo(() => {
    if (sidebarBehavior === 'hidden') {
      return 0;
    }
    // expandable
    return sidebarExpanded
      ? LAYOUT_DIMENSIONS.sidebarWidthExpanded
      : LAYOUT_DIMENSIONS.sidebarWidthCollapsed;
  }, [sidebarBehavior, sidebarExpanded]);

  // Header height based on sidebar visibility (mobile uses smaller header)
  const headerHeight = showTabBar
    ? LAYOUT_DIMENSIONS.headerHeightMobile
    : LAYOUT_DIMENSIONS.headerHeightDesktop;

  // Drawer items default to sidebar items
  const effectiveDrawerItems = drawerItems ?? sidebarItems;

  // Sidebar is collapsed when user has collapsed it
  const sidebarCollapsed = sidebarBehavior === 'expandable' && !sidebarExpanded;

  return (
    <AppShellContext.Provider value={contextValue}>
      {/*
       * The shell is pinned to the viewport height so <main> — not the document —
       * is the scroller (layout-wireframes.md: "SCROLLABLE CONTENT … overflow-y-auto").
       * With only `min-h-screen`, <main> grew to fit its content and never
       * scrolled, but its `overflow-y-auto` still made it the scroll container
       * for everything inside, so every `sticky` descendant was inert. Bounded,
       * a page's `sticky top-6` pins 24px below the header, as authored.
       * `min-h-0` on the body lets the flex column shrink below its content.
       */}
      <div
        className={`private-layout flex h-dvh flex-col bg-contrast-950 ${className}`}
      >
        {/* Skip to content link - visible on focus for keyboard navigation */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:rounded-md focus:bg-contrast-600 focus:px-4 focus:py-2 focus:text-neutral-50 focus:outline-none focus-visible:ring-state-focus"
        >
          Skip to content
        </a>

        <ImpersonationBanner
          onHeightChange={setBannerHeight}
          className="private-layout__banner fixed left-0 right-0 top-0 z-40"
        />

        {/* Sidebar - renders when sidebar behavior is not hidden */}
        {showSidebar && sidebarItems.length > 0 && (
          <aside
            className="private-layout__sidebar fixed bottom-0 left-0 top-0 z-30 border-r border-stroke-default bg-contrast-700 transition-[width] duration-normal ease-out"
            style={{
              width: sidebarWidth,
              top: bannerHeight,
            }}
          >
            <Sidebar
              items={sidebarItems}
              brand={sidebarBrand}
              activeItemId={activeSidebarItemId}
              onItemClick={onSidebarItemClick ?? (() => {})}
              collapsed={sidebarCollapsed}
              showViewMode={isDashboard && dashboardViewMode !== null}
              viewMode={dashboardViewMode ?? 'default'}
              onViewModeChange={onDashboardViewModeChange ?? undefined}
              userRole={user?.role ?? 'client'}
            />
          </aside>
        )}

        {/* Header - positioned after sidebar when sidebar is visible */}
        <header
          className="private-layout__header fixed right-0 top-0 z-40 border-b border-stroke-default bg-contrast-600"
          style={{
            height: headerHeight,
            top: bannerHeight,
            left: showSidebar ? sidebarWidth : 0,
          }}
        >
          {header ?? (
            <Header
              variant="main"
              logo={logo}
              onMenuClick={showTabBar ? toggleMobileDrawer : toggleSidebar}
              hideBrandOnDesktop={showSidebar}
            />
          )}
        </header>

        {/* Main layout container */}
        <div
          className="private-layout__body flex min-h-0 flex-1"
          style={{
            paddingTop: headerHeight + bannerHeight,
            paddingBottom: tabBarVisible ? LAYOUT_DIMENSIONS.tabBarHeight : 0,
          }}
        >
          {/* Main content area */}
          <main
            id="main-content"
            // `relative` so absolutely positioned descendants (e.g. `sr-only`
            // inputs) are contained — and clipped — by this scroller. Without it
            // their containing block is the page, and they stretched the
            // document into a second, phantom scrollbar.
            className="private-layout__content relative flex-1 overflow-y-auto"
            style={{
              marginLeft: sidebarWidth,
            }}
          >
            {/* `min-h-full`, not `h-full`: <main> now has a fixed height, and
                `h-full` capped this wrapper to it — a longer page overflowed the
                box and its bottom padding ended up inside, leaving the last
                element flush with the viewport edge. */}
            <div className="private-layout__content-inner mx-auto min-h-full max-w-360 px-4 py-4 sm:px-6 sm:py-6 lg:px-8">
              <PaywallRouteGate>
                <Outlet />
              </PaywallRouteGate>
            </div>
          </main>
        </div>

        {/* Bottom Tab Bar - renders when sidebar is hidden and not in full-screen edit */}
        {tabBarVisible && tabBarItems.length > 0 && (
          <nav
            className="private-layout__tabbar fixed bottom-0 left-0 right-0 z-40 border-t border-stroke-default bg-contrast-700 pb-[env(safe-area-inset-bottom)]"
            style={{ height: LAYOUT_DIMENSIONS.tabBarHeight }}
          >
            <BottomTabBar
              items={tabBarItems}
              activeItemId={activeTabBarItemId}
              onItemClick={onTabBarItemClick ?? (() => {})}
              actionButton={tabBarActionButton}
            />
          </nav>
        )}

        {/* Mobile Drawer Backdrop - renders when tab bar is shown */}
        {showTabBar && mobileDrawerOpen && (
          <div
            className="private-layout__backdrop fixed inset-0 z-50 bg-scrim-strong transition-opacity duration-slow"
            onClick={() => setMobileDrawerOpen(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setMobileDrawerOpen(false);
            }}
            role="button"
            tabIndex={0}
            aria-label="Close drawer"
          />
        )}

        {/* Mobile Drawer - renders when tab bar is shown */}
        {showTabBar && (
          <aside
            className={`private-layout__drawer fixed bottom-0 left-0 top-0 z-50 bg-contrast-700 pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] transition-transform duration-slow ease-out ${
              mobileDrawerOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
            style={{
              width: LAYOUT_DIMENSIONS.mobileDrawerWidth,
              maxWidth: `${LAYOUT_DIMENSIONS.mobileDrawerMaxWidthPercent}vw`,
            }}
          >
            <MobileDrawer
              isOpen={mobileDrawerOpen}
              onClose={() => setMobileDrawerOpen(false)}
              items={effectiveDrawerItems}
              activeItemId={activeSidebarItemId}
              onItemClick={onSidebarItemClick ?? (() => {})}
              onLogout={onLogout ?? (() => {})}
              logoutLabel={logoutLabel}
              logo={logo}
              showViewMode={isDashboard && dashboardViewMode !== null}
              viewMode={dashboardViewMode ?? 'default'}
              onViewModeChange={onDashboardViewModeChange ?? (() => {})}
            />
          </aside>
        )}
      </div>
    </AppShellContext.Provider>
  );
}

export default PrivateLayout;
